import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroButtons from "@/components/ui/HeroButtons";
import { useHeroContent, type HeroButton, type HeroSlideContent } from "@/hooks/useHeroContent";
import { useIsMobile } from "@/hooks/use-mobile";
import { modernHeroSources } from "@/lib/heroPreload";
import { homepageImageBackup } from "@/lib/homepageImages";
import { useInteractionReady } from "@/components/ui/InteractionGate";

type FallbackSlide = {
  image: string;
  imageMobile?: string | undefined;
  title?: string | undefined;
  subtitle?: string | undefined;
  cta?: ReactNode | undefined;
};

type Props = {
  cmsSlug?: string | undefined;
  slides: FallbackSlide[];
  children?: ReactNode;
  autoPlay?: boolean;
  interval?: number;
  /** Use images as-is (skip the optimized copies) — for pages whose photos have no prepared versions. */
  optimize?: boolean;
  /** Optional background video shown instead of the slide image (muted autoplay loop). */
  video?: string | undefined;
};

function imageFor(slide: HeroSlideContent | FallbackSlide, mobile: boolean) {
  if ("image_desktop" in slide || "image_mobile" in slide) {
    return mobile
      ? slide.image_mobile || slide.image_desktop || ""
      : slide.image_desktop || slide.image_mobile || "";
  }
  const fallback = slide as FallbackSlide;
  return mobile ? fallback.imageMobile || fallback.image : fallback.image || fallback.imageMobile || "";
}

/** Inset, bright page banner used only by the alternate shopper-first layout. */
export default function CarmaxPageHero({ cmsSlug, slides, children, autoPlay = true, interval = 6000, optimize = true, video }: Props) {
  const isMobile = useIsMobile();
  const engaged = useInteractionReady();
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  // Muted background video ignores the OS motion setting (Remote Desktop
  // reports reduced motion); only Save-Data mode skips it.
  const [saveData, setSaveData] = useState(false);
  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    setSaveData(conn?.saveData === true);
  }, []);
  const playVideo = engaged && !saveData;
  const { data: cms } = useHeroContent(cmsSlug ?? "", "hero_slides");
  const savedSlides = cmsSlug ? (cms?.slides ?? []).filter((slide) => slide.image_desktop || slide.image_mobile) : [];
  const activeSlides: Array<HeroSlideContent | FallbackSlide> = savedSlides.length > 0 ? savedSlides : slides;
  const [current, setCurrent] = useState(0);
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Autoplay policy requires the muted attribute at parse time; force it and
  // kick playback explicitly so the background video reliably starts.
  useLayoutEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!playVideo) { v.pause(); return; }
    v.muted = true;
    v.setAttribute("muted", "");
    v.load();
    v.play().catch(() => {});
  }, [video, playVideo]);

  useEffect(() => {
    setCurrent(0);
  }, [cmsSlug, activeSlides.length]);

  useEffect(() => {
    if (!engaged || reducedMotion || !autoPlay || activeSlides.length < 2 || isMobile) return;
    const timer = window.setInterval(() => setCurrent((value) => (value + 1) % activeSlides.length), cms?.layout?.interval_ms ?? interval);
    return () => window.clearInterval(timer);
  }, [engaged, reducedMotion, activeSlides.length, autoPlay, cms?.layout?.interval_ms, interval, isMobile]);

  const slide = activeSlides[current];
  if (!slide) return null;
  const image = imageFor(slide, isMobile);
  const imageSources = modernHeroSources(imageFor(slide, false), imageFor(slide, true), optimize, cmsSlug === "home");
  const useBackup = cmsSlug === "home" && failedImage === imageSources.desktop;

  const move = (direction: -1 | 1) => {
    setCurrent((value) => (value + direction + activeSlides.length) % activeSlides.length);
  };

  return (
    <section className="container pb-3 pt-5 md:pb-5 md:pt-7">
      <div className="relative min-h-[320px] overflow-hidden rounded-2xl bg-secondary md:min-h-[400px]">
        {video ? (
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay={playVideo}
            muted
            loop
            playsInline
            preload="none"
            poster={image || undefined}
            aria-hidden="true"
          >
            {playVideo && <source src={video} type="video/mp4" />}
          </video>
        ) : image && (
          <picture>
          {!useBackup && <source media="(max-width: 767px)" srcSet={imageSources.mobile} />}
          <img
            key={imageSources.desktop}
            ref={(target) => {
              if (cmsSlug === "home" && target?.complete && target.naturalWidth === 0) {
                setFailedImage(imageSources.desktop);
              }
            }}
            src={useBackup ? homepageImageBackup(image) : imageSources.desktop}
            alt=""
            width={1600}
            height={640}
            className="absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
            loading="eager"
            onError={(event) => {
              const target = event.currentTarget;
              if (cmsSlug === "home") {
                setFailedImage(imageSources.desktop);
                return;
              }
              if (target.dataset['fallback'] === "1" || target.src === image) return;
              target.dataset['fallback'] = "1";
              target.parentElement?.querySelector("source")?.removeAttribute("srcset");
              target.src = image;
            }}
          />
          </picture>
        )}
        {/* All slides share one cell so the banner keeps the tallest slide's height and never jumps */}
        <div className="relative z-10 grid min-h-[320px] grid-cols-1 md:min-h-[400px]">
          {activeSlides.map((item, index) => {
            const isActive = index === current;
            const itemImage = imageFor(item, isMobile);
            const itemButtons = "buttons" in item ? item.buttons : undefined;
            const itemCta = "cta" in item ? item.cta : children;
            const Heading: "h1" | "div" = isActive ? "h1" : "div";
            return (
              <div
                key={index}
                aria-hidden={!isActive}
                className={"flex max-w-2xl flex-col justify-center px-6 py-12 md:px-10 row-start-1 col-start-1" + (isActive ? "" : " invisible")}
              >
                <div className={itemImage ? "max-w-xl rounded-2xl bg-secondary/35 p-5 shadow-sm backdrop-blur-md md:p-7" : undefined}>
                  {item.title && (
                    <Heading className="font-heading text-4xl font-bold uppercase leading-none text-primary-foreground md:text-5xl" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>{item.title}</Heading>
                  )}
                  {item.subtitle && (
                    <p className="mt-3 max-w-xl text-base font-medium text-primary-foreground/95 md:text-lg" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{item.subtitle}</p>
                  )}
                  {(itemButtons?.length || itemCta) && (
                    <div className="mt-6">
                      <HeroButtons buttons={itemButtons as HeroButton[] | undefined} fallback={itemCta} className="justify-start" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {activeSlides.length > 1 && (
          <>
            <Button type="button" variant="secondary" size="icon" className="absolute bottom-5 right-16 z-20 rounded-full" onClick={() => move(-1)} aria-label="Previous slide">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button type="button" variant="secondary" size="icon" className="absolute bottom-5 right-5 z-20 rounded-full" onClick={() => move(1)} aria-label="Next slide">
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div className="absolute bottom-4 left-3 z-20 flex md:left-7" aria-label="Banner slides">
              {activeSlides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrent(index)}
                  className="flex h-11 min-w-11 items-center justify-center rounded-full px-1"
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === current ? "true" : undefined}
                >
                  <span
                    aria-hidden
                    className={`block h-2 rounded-full transition-all ${index === current ? "w-8 bg-primary" : "w-2 bg-primary-foreground/60"}`}
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}